(function () {
  const root = document.querySelector(".animal-list[data-section-id]");
  if (!root) return;

  const shopDomain = root.getAttribute("data-shop-domain");
  const apiVersion = root.getAttribute("data-api-version") || "2026-01";
  const metaobjectType = root.getAttribute("data-metaobject-type") || "animal_list";
  const moneyFormat = root.getAttribute("data-money-format") || "{{amount}}";
  const token = root.getAttribute("data-storefront-token") || "";
  const PAGE_SIZE = parseInt(root.getAttribute("data-page-size") || "24", 10);

  const storeSelect = root.querySelector('[data-filter="store"]');
  const catSelect = root.querySelector('[data-filter="category"]');
  const subSelect = root.querySelector('[data-filter="subcategory"]');
  const searchInput = root.querySelector('[data-filter="search"]');
  const clearBtn = root.querySelector("[data-clear]");
  const empty = root.querySelector("[data-empty]");
  const countEl = root.querySelector("[data-results-count]");
  const groupsHost = root.querySelector("[data-groups]");
  const loadingEl = root.querySelector("[data-loading]");
  const sentinel = root.querySelector("[data-sentinel]");
  const viewButtons = Array.from(root.querySelectorAll(".view-toggle__btn[data-view]"));

  const DEBUG = new URLSearchParams(location.search).get("debug_animals") === "1";

  const debugState = {
    loaded: 0,
    rendered: 0,
    skippedOutOfStock: 0,
    parseErrors: 0,
    examples: [],
  };

  // Custom display order for main categories
  const CATEGORY_ORDER = ["Lizards", "Snakes", "Tortoises & turtles", "Amphibians", "Invertebrates"];

  // Priority used for ordering cards
  const CATEGORY_PRIORITY = {
    Lizards: 1,
    Snakes: 2,
    "Tortoises & turtles": 3,
    Amphibians: 4,
    Invertebrates: 5,
  };

  // Prefer loading these categories first (initial warm-up)
  const PREFETCH_TARGETS = {
    Lizards: 24, // start with 24 lizards if possible
  };

  // Placeholder image (used when metaobject image is blank)
  const PLACEHOLDER_IMAGE = "https://cdn.shopify.com/s/files/1/0667/4664/6744/files/photo-coming-soon.jpg";

  // "New in" settings (GLOBAL via metaobject field date_in_stock)
  const NEW_IN_DAYS = 28;
  const NEW_IN_MS = NEW_IN_DAYS * 24 * 60 * 60 * 1000;
  const DATE_IN_STOCK_KEY = "date_in_stock";

  if (!token) {
    groupsHost.innerHTML =
      '<div style="padding:12px;border:2px solid #e11;border-radius:12px;background:#fff;">' +
      "<strong>Animal List error:</strong> Missing Storefront API public access token.<br><br>" +
      "Fix: Theme editor → this page’s Animal List section → paste the Headless channel <em>Public access token</em>." +
      "</div>";
    return;
  }

  const endpoint = `https://${shopDomain}/api/${apiVersion}/graphql.json`;

  // Loaded items (as DOM cards)
  const cards = [];
  const taxonomy = new Map(); // cat -> Set(sub)
  const seenIds = new Set();

  // Pagination state
  let endCursor = null;
  let hasNextPage = true;
  let isLoading = false;

  // Total in-stock count computed separately
  let totalInStock = null;

  // Prevent observer from triggering loads while we prefetch
  let isPrefetching = false;

  function normalize(s) {
    return (s || "").toLowerCase().trim();
  }

  function escapeHtml(s) {
    return String(s || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function escapeAttr(s) {
    return escapeHtml(s).replaceAll("`", "&#096;");
  }

  function tryParseJSON(value) {
    if (value == null) return null;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return null;

    const v = value.trim();
    if (!v) return null;
    if ((v.startsWith("[") && v.endsWith("]")) || (v.startsWith("{") && v.endsWith("}"))) {
      try {
        return JSON.parse(v);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  function boolFromField(field) {
    const v = field && field.value != null ? String(field.value).toLowerCase().trim() : "";
    return v === "true" || v === "1" || v === "yes" || v === "on";
  }

  function parseOtherNames(field) {
    if (!field) return [];
    const json = tryParseJSON(field.value);
    if (Array.isArray(json)) return json.map(String);
    if (typeof field.value === "string" && field.value.trim()) {
      return field.value.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }

  function getFieldMap(metaobjectNode) {
    const map = new Map();
    (metaobjectNode.fields || []).forEach((f) => map.set(f.key, f));
    return map;
  }

  function getMetaobjectNameFromReference(ref) {
    if (!ref || !ref.fields) return "";
    const nameField = ref.fields.find((f) => f.key === "name");
    return nameField && nameField.value ? String(nameField.value) : ref.handle || "";
  }

  function parseMoneyAmount(value) {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;

    const s = String(value).trim();
    if (!s) return null;

    const json = tryParseJSON(s);
    if (json && typeof json === "object") {
      const amt = json.amount ?? json.Amount ?? null;
      if (amt != null) {
        const n = parseFloat(String(amt));
        return Number.isFinite(n) ? n : null;
      }
    }

    const cleaned = s.replace(/[^0-9.,-]/g, "");
    if (!cleaned) return null;

    let normalized = cleaned;
    if (cleaned.includes(",") && cleaned.includes(".")) normalized = cleaned.replace(/,/g, "");
    else if (cleaned.includes(",") && !cleaned.includes(".")) normalized = cleaned.replace(",", ".");

    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
  }

  // Supports {{amount}} and {{ amount }} plus common variants
  function formatMoney(amountDecimal) {
    if (amountDecimal == null) return "";
    const amount = Number(amountDecimal);
    if (!Number.isFinite(amount)) return "";

    const cents = Math.round(amount * 100);

    function formatWithDelimiters(centsValue, precision = 2, thousands = ",", decimal = ".") {
      const num = (centsValue / 100).toFixed(precision);
      const parts = num.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }

    const tokens = {
      "{{amount}}": formatWithDelimiters(cents, 2),
      "{{ amount }}": formatWithDelimiters(cents, 2),

      "{{amount_no_decimals}}": formatWithDelimiters(cents, 0),
      "{{ amount_no_decimals }}": formatWithDelimiters(cents, 0),

      "{{amount_with_comma_separator}}": formatWithDelimiters(cents, 2, ".", ","),
      "{{ amount_with_comma_separator }}": formatWithDelimiters(cents, 2, ".", ","),

      "{{amount_no_decimals_with_comma_separator}}": formatWithDelimiters(cents, 0, ".", ","),
      "{{ amount_no_decimals_with_comma_separator }}": formatWithDelimiters(cents, 0, ".", ","),

      "{{amount_with_apostrophe_separator}}": formatWithDelimiters(cents, 2, "'", "."),
      "{{ amount_with_apostrophe_separator }}": formatWithDelimiters(cents, 2, "'", "."),
    };

    let out = moneyFormat;
    Object.keys(tokens).forEach((k) => (out = out.split(k).join(tokens[k])));
    return out;
  }

  function extractMediaImageGidFromFileReference(field) {
    if (!field) return null;
    if (field.type === "file_reference" && typeof field.value === "string" && field.value.startsWith("gid://shopify/MediaImage/")) {
      return field.value.trim();
    }
    return null;
  }

  function getImageUrlFromField(field) {
    if (!field) return "";

    if (field.reference) {
      const r = field.reference;
      if (r.image && r.image.url) return r.image.url;
      if (r.url) return r.url;
    }

    if (field.references && field.references.edges) {
      for (const e of field.references.edges) {
        const n = e.node;
        if (!n) continue;
        if (n.image && n.image.url) return n.image.url;
        if (n.url) return n.url;
      }
    }

    if (typeof field.value === "string") {
      const v = field.value.trim();
      if (v.startsWith("http://") || v.startsWith("https://")) return v;
    }

    const json = tryParseJSON(field.value);
    if (json && typeof json === "object") {
      if (json.url && typeof json.url === "string") return json.url;
      if (json.image && json.image.url) return json.image.url;
    }

    return "";
  }

  function isNewInFromDateInStock(value) {
    if (!value) return false;
    const t = Date.parse(value);
    if (!Number.isFinite(t)) return false;
    return (Date.now() - t) <= NEW_IN_MS;
  }

  async function sfRequest(query, variables) {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query, variables }),
    });

    const json = await res.json();

    if (!res.ok) {
      console.error("Storefront API HTTP error:", res.status, res.statusText, json);
      throw new Error(`HTTP ${res.status} ${res.statusText}`);
    }

    if (json.errors && json.errors.length) {
      console.error("Storefront API GraphQL errors:", json.errors);
      throw new Error(json.errors.map((e) => e.message).join(" | "));
    }

    if (!json.data) {
      console.error("Storefront API: missing data:", json);
      throw new Error("Storefront API returned no data");
    }

    return json.data;
  }

  async function resolveMediaImagesById(ids) {
    if (!ids || !ids.length) return new Map();

    const query = `
      query ResolveImages($ids: [ID!]!) {
        nodes(ids: $ids) {
          __typename
          ... on MediaImage {
            id
            image { url altText width height }
          }
        }
      }
    `;

    const data = await sfRequest(query, { ids });
    const map = new Map();

    (data.nodes || []).forEach((n) => {
      if (n && n.__typename === "MediaImage" && n.image && n.image.url) map.set(n.id, n.image.url);
    });

    return map;
  }

  // --- UI helpers ---
  const storageKey = "animal_list_view";
  const mqMobile = window.matchMedia("(max-width: 769px)");

  function setView(mode, persist) {
    groupsHost.setAttribute("data-view-mode", mode);
    viewButtons.forEach((b) => {
      const active = b.dataset.view === mode;
      b.classList.toggle("is-active", active);
      b.setAttribute("aria-pressed", active ? "true" : "false");
    });
    if (persist) sessionStorage.setItem(storageKey, mode);
  }

  function applyResponsiveDefault() {
    if (mqMobile.matches) setView("grid", false);
    else {
      const saved = sessionStorage.getItem(storageKey);
      if (saved === "grid" || saved === "list") setView(saved, false);
      else setView("list", false);
    }
  }

  mqMobile.addEventListener?.("change", applyResponsiveDefault);
  if (mqMobile.addListener) mqMobile.addListener(applyResponsiveDefault);

  viewButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      setView(btn.dataset.view, true);
      applyFilters();
    });
  });

  function upsertTaxonomy(cat, sub) {
    const c = cat || "Uncategorised";
    const s = sub || "Other";
    if (!taxonomy.has(c)) taxonomy.set(c, new Set());
    taxonomy.get(c).add(s);
  }

  function refillCategoryOptionsKeepSelection() {
    const current = catSelect.value;
    catSelect.innerHTML = "";
    catSelect.appendChild(new Option("All categories", "all"));

    Array.from(taxonomy.keys())
      .sort((a, b) => {
        const ia = CATEGORY_ORDER.indexOf(a);
        const ib = CATEGORY_ORDER.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return a.localeCompare(b);
      })
      .forEach((c) => catSelect.appendChild(new Option(c, c)));

    catSelect.value = [...catSelect.options].some((o) => o.value === current) ? current : "all";
  }

  function fillSubcategoryOptionsFor(categoryValue) {
    const current = subSelect.value;
    subSelect.innerHTML = "";
    subSelect.appendChild(new Option("All subcategories", "all"));

    if (categoryValue === "all") {
      subSelect.disabled = true;
      return;
    }

    Array.from(taxonomy.get(categoryValue) || [])
      .sort()
      .forEach((s) => subSelect.appendChild(new Option(s, s)));

    subSelect.disabled = false;
    subSelect.value = [...subSelect.options].some((o) => o.value === current) ? current : "all";
  }

  function groupAndRender(visibleCards) {
    visibleCards.sort((a, b) => {
      const pa = parseInt(a.dataset.catPriority || "999", 10);
      const pb = parseInt(b.dataset.catPriority || "999", 10);
      if (pa !== pb) return pa - pb;
      return (a.dataset.common || "").localeCompare(b.dataset.common || "");
    });

    const grouped = new Map();
    visibleCards.forEach((card) => {
      const cat = card.dataset.category || "Uncategorised";
      const sub = card.dataset.subcategory || "Other";
      if (!grouped.has(cat)) grouped.set(cat, new Map());
      if (!grouped.get(cat).has(sub)) grouped.get(cat).set(sub, []);
      grouped.get(cat).get(sub).push(card);
    });

    groupsHost.innerHTML = "";

    Array.from(grouped.keys())
      .sort((a, b) => {
        const ia = CATEGORY_ORDER.indexOf(a);
        const ib = CATEGORY_ORDER.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return a.localeCompare(b);
      })
      .forEach((cat) => {
        const subMap = grouped.get(cat);
        const catCount = [...subMap.values()].reduce((a, b) => a + b.length, 0);

        const details = document.createElement("details");
        details.className = "animal-group";
        details.open = true;

        const summary = document.createElement("summary");
        summary.className = "animal-group__summary";
        summary.innerHTML = `
          <span class="animal-group__left">
            <span class="animal-group__chev" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="18" height="18" fill="none">
                <path d="M5 7.5l5 5 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="animal-group__title">${escapeHtml(cat)}</span>
          </span>
          <span class="animal-group__count">${catCount}</span>
        `;
        details.appendChild(summary);

        Array.from(subMap.keys())
          .sort()
          .forEach((sub) => {
            const wrapper = document.createElement("div");
            wrapper.className = "animal-subgroup";

            const header = document.createElement("div");
            header.className = "animal-subgroup__header";
            header.textContent = sub;
            wrapper.appendChild(header);

            const grid = document.createElement("div");
            grid.className = "animal-subgroup__grid";
            subMap.get(sub).forEach((card) => grid.appendChild(card));

            wrapper.appendChild(grid);
            details.appendChild(wrapper);
          });

        groupsHost.appendChild(details);
      });
  }

  function updateCountText(visibleCount) {
    if (!countEl) return;
    if (totalInStock == null) countEl.textContent = `${visibleCount} animals shown`;
    else countEl.textContent = `Showing ${visibleCount} of ${totalInStock} in stock`;
  }

  function applyFilters() {
    const store = storeSelect.value;
    const cat = catSelect.value;
    const sub = subSelect.value;
    const q = normalize(searchInput.value);

    const visible = [];

    cards.forEach((card) => {
      const inN = card.dataset.storeNorthampton === "true";
      const inT = card.dataset.storeTowcester === "true";

      const storeOk = store === "all" || (store === "northampton" && inN) || (store === "towcester" && inT);
      const catOk = cat === "all" || card.dataset.category === cat;
      const subOk = sub === "all" || card.dataset.subcategory === sub;

      const haystack = [
        card.dataset.common,
        card.dataset.sci,
        card.dataset.other,
        card.dataset.desc,
        card.dataset.category,
        card.dataset.subcategory,
        card.dataset.age,
      ]
        .map(normalize)
        .join(" ");

      const searchOk = !q || haystack.includes(q);

      const show = storeOk && catOk && subOk && searchOk;
      card.hidden = !show;
      if (show) visible.push(card);
    });

    if (visible.length > 0) {
      empty.hidden = true;
      groupAndRender(visible);
    } else {
      groupsHost.innerHTML = "";
      empty.hidden = false;
    }

    updateCountText(visible.length);
  }

  function storePill(label) {
    return `
      <span class="store">
        <span class="store__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
            <path d="M3 10h18l-1.2 10.2a2 2 0 0 1-2 1.8H6.2a2 2 0 0 1-2-1.8L3 10Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
            <path d="M7 10V8.2A5 5 0 0 1 12 3a5 5 0 0 1 5 5.2V10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </span>
        ${escapeHtml(label)}
      </span>
    `;
  }

  function buildCard(animal) {
    const el = document.createElement("article");
    el.className = "animal-card";
    el.setAttribute("data-animal", "");

    el.dataset.category = animal.category_label || "";
    el.dataset.catPriority = CATEGORY_PRIORITY[animal.category_label] || 999;
    el.dataset.subcategory = animal.subcategory || "";
    el.dataset.storeNorthampton = String(!!animal.in_stock_northampton);
    el.dataset.storeTowcester = String(!!animal.in_stock_towcester);
    el.dataset.common = animal.common_name || "";
    el.dataset.sci = animal.scientific_name || "";
    el.dataset.other = (animal.other_common_names || []).join(", ");
    el.dataset.desc = animal.short_description || "";
    el.dataset.difficulty = (animal.difficulty || "").toLowerCase();
    el.dataset.age = animal.age || "";

    const imageToUse = animal.image_url || PLACEHOLDER_IMAGE;

    const imgHtml = `
      <img
        class="animal-card__img"
        loading="lazy"
        alt="${escapeHtml(animal.common_name || "Animal photo coming soon")}"
        src="${escapeAttr(imageToUse)}"
      >
    `;

    const onSale = animal.offer_price != null && animal.price != null && animal.offer_price < animal.price;

    el.innerHTML = `
      <div class="animal-card__media">
        <div class="animal-card__image">
          ${imgHtml}
          ${animal.is_new_in ? `<span class="badge badge--new" aria-label="New in">New</span>` : ``}
        </div>
      </div>

      <div class="animal-card__body">
        <div class="animal-card__top">
          <div class="animal-card__names">
            <h3 class="animal-card__title">${escapeHtml(animal.common_name || "")}</h3>
            ${animal.scientific_name ? `<div class="animal-card__sci"><em>${escapeHtml(animal.scientific_name)}</em></div>` : ``}
            ${(animal.other_common_names && animal.other_common_names.length)
              ? `<div class="animal-card__other">Also known as: ${escapeHtml(animal.other_common_names.join(", "))}</div>`
              : ``}
          </div>

          <div class="animal-card__price">
            ${onSale
              ? `<div class="price price--offer">${escapeHtml(formatMoney(animal.offer_price))}</div>
                 <div class="price price--was">${escapeHtml(formatMoney(animal.price))}</div>`
              : (animal.price != null ? `<div class="price price--normal">${escapeHtml(formatMoney(animal.price))}</div>` : ``)
            }
          </div>
        </div>

        ${animal.short_description ? `<div class="animal-card__desc">${escapeHtml(animal.short_description)}</div>` : ``}

        <div class="animal-card__row">
          <span style="display:flex;gap:.5rem;flex-wrap:wrap;">
            ${animal.difficulty ? `<span class="difficulty difficulty--${escapeHtml((animal.difficulty||"").toLowerCase())}">${escapeHtml(animal.difficulty)}</span>` : ``}
            ${animal.age ? `<span class="age">Age: ${escapeHtml(animal.age)}</span>` : ``}
          </span>

          <span class="animal-card__stores">
            ${animal.in_stock_northampton ? storePill("Northampton") : ""}
            ${animal.in_stock_towcester ? storePill("Towcester") : ""}
          </span>
        </div>

        ${(animal.care_sheet_link || animal.setup_link) ? `
          <div class="animal-card__links">
            ${animal.care_sheet_link ? `<a href="${escapeAttr(animal.care_sheet_link)}" class="animal-card__link" target="_blank" rel="noopener">Care sheet</a>` : ``}
            ${animal.setup_link ? `<a href="${escapeAttr(animal.setup_link)}" class="animal-card__link" target="_blank" rel="noopener">Setup</a>` : ``}
          </div>
        ` : ``}
      </div>
    `;

    return el;
  }

  function getCategoryCountsFromCards() {
    const counts = {};
    for (const c of cards) {
      const cat = c.dataset.category || "Uncategorised";
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }

  function meetsPrefetchTargets() {
    const counts = getCategoryCountsFromCards();
    for (const [cat, min] of Object.entries(PREFETCH_TARGETS)) {
      if ((counts[cat] || 0) < min) return false;
    }
    return true;
  }

  async function computeTotalInStockCount() {
    const query = `
      query CountInStock($first: Int!, $after: String) {
        metaobjects(type: "${metaobjectType}", first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          edges { node { id fields { key value } } }
        }
      }
    `;

    let after = null;
    let hasNext = true;
    let count = 0;

    while (hasNext) {
      const data = await sfRequest(query, { first: 100, after });
      const conn = data.metaobjects;
      const edges = conn.edges || [];

      for (const { node } of edges) {
        if (!node) continue;
        const fm = getFieldMap(node);
        const inN = boolFromField(fm.get("in_stock_northampton"));
        const inT = boolFromField(fm.get("in_stock_towcester"));
        if (inN || inT) count++;
      }

      hasNext = !!conn.pageInfo.hasNextPage;
      after = conn.pageInfo.endCursor;
    }

    totalInStock = count;
    updateCountText(cards.filter((c) => !c.hidden).length);
  }

  async function fetchBatch() {
    if (isLoading || !hasNextPage) return;

    isLoading = true;
    loadingEl.hidden = false;

    const query = `
      query AnimalList($first: Int!, $after: String) {
        metaobjects(type: "${metaobjectType}", first: $first, after: $after, sortKey: "updated_at", reverse: true) {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id
              handle
              fields {
                key
                type
                value
                reference {
                  ... on Metaobject { type handle fields { key value } }
                  ... on MediaImage { image { url altText width height } }
                  ... on GenericFile { url }
                }
                references(first: 20) {
                  edges {
                    node {
                      ... on Metaobject { type handle fields { key value } }
                      ... on MediaImage { image { url altText width height } }
                      ... on GenericFile { url }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data = await sfRequest(query, { first: PAGE_SIZE, after: endCursor });
      const conn = data.metaobjects;
      const edges = conn.edges || [];

      const batchAnimals = [];
      const imageIdsToResolve = [];

      for (const { node } of edges) {
        if (!node || !node.id || seenIds.has(node.id)) continue;
        seenIds.add(node.id);

        debugState.loaded++;

        const fm = getFieldMap(node);

        const inN = boolFromField(fm.get("in_stock_northampton"));
        const inT = boolFromField(fm.get("in_stock_towcester"));
        const isEligibleNow = !!(inN || inT);

        if (!isEligibleNow) {
          debugState.skippedOutOfStock++;
          continue;
        }

        try {
          const commonName = (fm.get("common_name")?.value || "").trim() || node.handle;
          const scientificName = (fm.get("scientific_name")?.value || "").trim();
          const otherNames = parseOtherNames(fm.get("other_common_names"));
          const shortDesc = (fm.get("short_description")?.value || "").trim();
          const age = (fm.get("age")?.value || "").trim();
          const difficulty = (fm.get("difficulty")?.value || "").trim();

          const priceRaw = fm.get("price")?.value;
          const offerRaw = fm.get("offer_price")?.value;
          const price = parseMoneyAmount(priceRaw);
          const offer = parseMoneyAmount(offerRaw);

          const dateInStock = (fm.get(DATE_IN_STOCK_KEY)?.value || "").trim();
          const isNewIn = isEligibleNow && isNewInFromDateInStock(dateInStock);

          const imageField = fm.get("image");
          let imageUrl = getImageUrlFromField(imageField);
          const mediaImageGid = extractMediaImageGidFromFileReference(imageField);
          if (!imageUrl && mediaImageGid) imageIdsToResolve.push(mediaImageGid);

          const categoryRef = fm.get("category")?.reference;
          const subcategoryRef = fm.get("animal_subcategory")?.reference;

          let categoryLabel = getMetaobjectNameFromReference(categoryRef);
          if (categoryLabel === "Chelonias") categoryLabel = "Tortoises & turtles";
          const subcategoryLabel = getMetaobjectNameFromReference(subcategoryRef);

          const careLink = (fm.get("care_sheet_link")?.value || "").trim();
          const setupLink = (fm.get("setup_link")?.value || "").trim();

          batchAnimals.push({
            _handle: node.handle,
            _image_gid: mediaImageGid || null,

            common_name: commonName,
            scientific_name: scientificName,
            other_common_names: otherNames,
            short_description: shortDesc,
            image_url: imageUrl,
            category_label: categoryLabel || "Uncategorised",
            subcategory: subcategoryLabel || "Other",
            in_stock_northampton: inN,
            in_stock_towcester: inT,
            price: price,
            offer_price: offer,
            care_sheet_link: careLink,
            setup_link: setupLink,
            difficulty: difficulty,
            age: age,

            date_in_stock: dateInStock,
            is_new_in: isNewIn,
          });
        } catch (err) {
          debugState.parseErrors++;
          console.error("Skipping animal due to parse/render error:", node.handle, err);
        }
      }

      // Resolve MediaImage GIDs -> URLs in one request
      const uniqueImageIds = Array.from(new Set(imageIdsToResolve));
      let imageUrlMap = new Map();
      if (uniqueImageIds.length) imageUrlMap = await resolveMediaImagesById(uniqueImageIds);

      for (const animal of batchAnimals) {
        if (!animal.image_url && animal._image_gid && imageUrlMap.has(animal._image_gid)) {
          animal.image_url = imageUrlMap.get(animal._image_gid);
        }

        const card = buildCard(animal);
        cards.push(card);
        debugState.rendered++;
        upsertTaxonomy(animal.category_label, animal.subcategory);
      }

      hasNextPage = !!conn.pageInfo.hasNextPage;
      endCursor = conn.pageInfo.endCursor;

      refillCategoryOptionsKeepSelection();
      fillSubcategoryOptionsFor(catSelect.value);
      applyFilters();
    } catch (e) {
      console.error("Animal List fetchBatch failed:", e);
      groupsHost.innerHTML =
        '<div style="padding:12px;border:2px solid #e11;border-radius:12px;background:#fff;">' +
        "<strong>Animal List error:</strong> " +
        escapeHtml(e.message || "Storefront API request failed") +
        "</div>";
    } finally {
      isLoading = false;
      loadingEl.hidden = true;
    }
  }

  async function initialPrefetch() {
    isPrefetching = true;

    let safety = 0;
    while (!meetsPrefetchTargets() && hasNextPage && safety < 20) {
      await fetchBatch();
      safety++;
    }

    isPrefetching = false;
  }

  // --- Filters wiring ---
  catSelect.addEventListener("change", () => {
    fillSubcategoryOptionsFor(catSelect.value);
    subSelect.value = "all";
    applyFilters();
  });
  subSelect.addEventListener("change", applyFilters);
  storeSelect.addEventListener("change", applyFilters);

  // Search auto-loads more pages until it finds matches
  searchInput.addEventListener("input", async () => {
    applyFilters();

    const q = normalize(searchInput.value);
    if (!q) return;

    let attempts = 0;
    while (attempts < 6 && hasNextPage) {
      const visibleNow = cards.filter((c) => !c.hidden).length;
      if (visibleNow > 0) break;
      await fetchBatch();
      applyFilters();
      attempts++;
    }
  });

  clearBtn.addEventListener("click", () => {
    storeSelect.value = "all";
    catSelect.value = "all";
    fillSubcategoryOptionsFor("all");
    subSelect.value = "all";
    searchInput.value = "";
    applyFilters();
  });

  // Infinite scroll observer
  const io = new IntersectionObserver(
    (entries) => {
      if (isPrefetching) return;
      for (const e of entries) {
        if (e.isIntersecting) fetchBatch();
      }
    },
    { rootMargin: "800px 0px" }
  );
  io.observe(sentinel);

  // Start
  applyResponsiveDefault();
  updateCountText(0);
  computeTotalInStockCount();
  initialPrefetch();
})();
